import React, { useEffect, useState, useCallback } from "react";
import { Space, message, Table, Button, Typography, Alert, Modal } from "antd";
import Util from "../../Util";
import { downloadAsCsv } from "./CommonUtils";

const { Title, Link } = Typography;

function PaymentPending() {
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);
  const [noVendorIdTable, setNoVendorIdTable] = useState([]);
  const [lessHundredTable, setLessHundredTable] = useState([]);
  const [showingTable, setshowingTable] = useState([]);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [detailModalRow, setDetailModalRow] = useState(null);
  const [tablePagination, setTablePagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const loadPendingData = useCallback(async () => {
    setLoading(true);
    const data = await Util.sendGet("/api/moresheetStaff/getHytextsTopay", {});
    if (!data.success) {
      messageApi.error("มีข้อผิดพลาด");
      setLoading(false);
      return;
    }
    const payments = data.data.payments.sort((a, b) => {
      if (a.year < b.year) return -1;
      if (a.year > b.year) return 1;
      if (a.month === b.month) return 0;
      return a.month < b.month ? -1 : 1;
    });
    const relaventSellers = data.data.relaventSellers;

    // Group by facebookID
    const facebookIDGroup = {}; // 100% revenue, need to calculate 60%
    for (const row of payments) {
      if (!facebookIDGroup.hasOwnProperty(row.author)) {
        facebookIDGroup[row.author] = {
          total: 0,
          paymentIds: [],
          paymentDetail: [],
        };
      }
      facebookIDGroup[row.author].total += row.total;
      facebookIDGroup[row.author].paymentIds.push(row._id);
      facebookIDGroup[row.author].paymentDetail.push({
        month: `${row.year}-${row.month}`,
        total: row.total.toFixed(2),
        payout: (row.total * 0.6).toFixed(2),
      });
    }

    // Group by Vendor ID / split those without
    const noVendorIdTable = [];
    const showingTable = [];
    const lessHundredTable = [];

    for (const seller of relaventSellers) {
      if (!facebookIDGroup.hasOwnProperty(seller.facebookID)) continue;
      const { total, paymentIds, paymentDetail } =
        facebookIDGroup[seller.facebookID];
      const payout = total * 0.6;
      if (!seller.htiVendorId) {
        noVendorIdTable.push({
          codeName: seller.codeName,
          facebookID: seller.facebookID,
          paymentIds,
          peopleID: seller.peopleID,
          realName: seller.realName,
          revenue: total.toFixed(2),
          payout: payout.toFixed(2),
          paymentDetail,
        });
        continue;
      }
    }

    const vendorIdGroup = {};
    for (const seller of relaventSellers) {
      if (
        !facebookIDGroup.hasOwnProperty(seller.facebookID) ||
        !seller.htiVendorId
      )
        continue;
      const { total, paymentIds, paymentDetail } =
        facebookIDGroup[seller.facebookID];
      const splitBank = seller.bankInfo.split(",");
      const payBank = splitBank.length === 2 ? splitBank[0] : "Invalid";
      if (!vendorIdGroup.hasOwnProperty(seller.htiVendorId)) {
        vendorIdGroup[seller.htiVendorId] = {
          total: 0,
          paymentIds: [],
          paymentDetail: [],
          htiVendorName: seller.htiVendorName,
          peopleID: seller.peopleID,
          realName: seller.realName,
          payBank,
        };
      }
      vendorIdGroup[seller.htiVendorId].total += total;
      vendorIdGroup[seller.htiVendorId].paymentIds.push(...paymentIds);
      vendorIdGroup[seller.htiVendorId].paymentDetail.push(...paymentDetail);
    }

    Object.entries(vendorIdGroup).forEach(([key, val]) => {
      const payout = val.total * 0.6;
      const vendorData = {
        ...val,
        htiVendorId: key,
        payout: payout.toFixed(2),
        total: val.total.toFixed(2),
        scbNonScb: val.payBank === "ธ. ไทยพาณิชย์" ? "SCB" : "Non-SCB",
      };
      if (payout < 100) {
        lessHundredTable.push(vendorData);
      }
      showingTable.push(vendorData);
    });

    setLoading(false);
    return { noVendorIdTable, lessHundredTable, showingTable };
  }, [messageApi]);

  useEffect(() => {
    const fetchData = async () => {
      const result = await loadPendingData();
      if (result) {
        setNoVendorIdTable(result.noVendorIdTable);
        setLessHundredTable(result.lessHundredTable);
        setshowingTable(result.showingTable);
      }
    };
    fetchData();
  }, [loadPendingData]);

  const showDetailModal = (row) => {
    setDetailModalRow(row);
    setDetailModalVisible(true);
  };

  const closeDetailModal = () => {
    setDetailModalVisible(false);
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setTablePagination(pagination);
  };

  const VendorTables = () => {
    const validVendorColumns = [
      { title: "Vendor Id", dataIndex: "htiVendorId", key: "htiVendorId" },
      {
        title: "Vendor Name",
        dataIndex: "htiVendorName",
        key: "htiVendorName",
      },
      { title: "เลขบัตรประชาชน", dataIndex: "peopleID", key: "peopleID" },
      { title: "ชื่อจริง", dataIndex: "realName", key: "realName" },
      {
        title: "ยอดขาย",
        dataIndex: "total",
        key: "total",
        render: (text, row) => (
          <Link onClick={() => showDetailModal(row)}>{text}</Link>
        ),
      },
      {
        title: "ยอดจ่าย(60%)",
        dataIndex: "payout",
        key: "payout",
        render: (text, row) => (
          <Link onClick={() => showDetailModal(row)}>{text}</Link>
        ),
      },
      { title: "ธนาคาร", dataIndex: "payBank", key: "payBank" },
      { title: "SCB or Non-SCB", dataIndex: "scbNonScb", key: "scbNonScb" },
    ];

    return (
      <React.Fragment>
        <Title level={4} type="success">
          รายการพร้อมทำจ่าย ({showingTable.length})
        </Title>
        <Button
          type="primary"
          onClick={() =>
            downloadAsCsv(
              showingTable,
              validVendorColumns,
              "pending_payable_gt100.csv"
            )
          }
        >
          Download as CSV
        </Button>
        <Table
          dataSource={showingTable}
          columns={validVendorColumns}
          pagination={{
            ...tablePagination,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100", "200"],
            total: showingTable.length,
          }}
          onChange={handleTableChange}
          rowKey="htiVendorId"
        />
      </React.Fragment>
    );
  };

  return (
    <Space direction="vertical" size={12}>
      {contextHolder}
      <Modal
        title="รายละเอียดยอดขายสะสม"
        open={detailModalVisible}
        onOk={closeDetailModal}
        onCancel={closeDetailModal}
        footer={[
          <Button key="ok" type="primary" onClick={closeDetailModal}>
            OK
          </Button>,
        ]}
      >
        <p>
          {detailModalRow?.htiVendorId}: {detailModalRow?.htiVendorName}
        </p>
        <Table
          dataSource={detailModalRow?.paymentDetail}
          columns={[
            { title: "เดือน", dataIndex: "month", key: "month" },
            { title: "ยอดขาย", dataIndex: "total", key: "total" },
            {
              title: "ยอดจ่าย (60%)",
              dataIndex: "payout",
              key: "payout",
            },
          ]}
          pagination={false}
          size="small"
        />
        <p>รวมยอดจ่าย {detailModalRow?.payout} บาท</p>
      </Modal>
      {loading && <p>Loading...</p>}
      {!loading && (
        <React.Fragment>
          {noVendorIdTable.length > 0 && (
            <Alert
              message={`มี ${noVendorIdTable.length} รายการที่ไม่มี Vendor ID`}
              description="กรุณาไปที่หน้า '2. รายได้ไม่มี Vendor Id' เพื่อจัดการ"
              type="error"
              showIcon
            />
          )}
          {lessHundredTable.length > 0 && (
            <Alert
              message={`มี ${lessHundredTable.length} รายการที่ยอดจ่ายน้อยกว่า 100 บาท`}
              description="กรุณาไปที่หน้า '3. รายการต่ำกว่า 100 บาท' เพื่อจัดการหากไม่ใช่การจ่ายโดยตั้งใจ"
              type="warning"
              showIcon
            />
          )}
          <VendorTables />
        </React.Fragment>
      )}
    </Space>
  );
}

export default PaymentPending;
